import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { Slot } from '@radix-ui/react-slot';
// Helper function to inject data-component-tag into rendered elements
function injectDataComponentTag(renderedElement, dataComponentTag, restProps) {
    // Automatically inject data-component-tag into the first/only element
    if (React.isValidElement(renderedElement)) {
        // Handle fragments specifically
        if (renderedElement.type === React.Fragment) {
            const children = React.Children.toArray(renderedElement.props.children);
            if (children.length > 0) {
                const firstChild = children[0];
                const restChildren = children.slice(1);
                if (React.isValidElement(firstChild)) {
                    // Only inject data-component-tag if the child doesn't already have one
                    const existingTag = firstChild.props['data-component-tag'];
                    const enhancedFirstChild = React.cloneElement(firstChild, {
                        ...firstChild.props,
                        ...getConditionalDataComponentTagProps(dataComponentTag, existingTag),
                    });
                    return (_jsx(Slot, { ...restProps, children: _jsxs(_Fragment, { children: [enhancedFirstChild, restChildren] }) }));
                }
            }
            return _jsx(Slot, { ...restProps, children: renderedElement });
        }
        // Handle single element
        // Only inject data-component-tag if the element doesn't already have one
        const existingTag = renderedElement.props['data-component-tag'];
        const enhancedElement = React.cloneElement(renderedElement, {
            ...renderedElement.props, // Preserve existing props
            ...getConditionalDataComponentTagProps(dataComponentTag, existingTag),
        });
        return _jsx(Slot, { ...restProps, children: enhancedElement });
    }
    return _jsx(Slot, { ...restProps, children: renderedElement });
}
// Helper functions for data-component-tag prop merging
function getDataComponentTagProps(dataComponentTag) {
    return dataComponentTag ? { 'data-component-tag': dataComponentTag } : {};
}
function getConditionalDataComponentTagProps(dataComponentTag, existingTag) {
    return dataComponentTag && !existingTag
        ? { 'data-component-tag': dataComponentTag }
        : {};
}
export const AsChildSlot = React.forwardRef((props, ref) => {
    const { asChild, children, customElement, customElementProps = {}, content, 'data-component-tag': dataComponentTag, ...restProps } = props;
    if (asChild && customElement) {
        // Handle string
        if (typeof customElement === 'string') {
            return (_jsx(Slot, { ref: ref, ...restProps, children: _jsx("span", { ...getDataComponentTagProps(dataComponentTag), children: customElement }) }));
        }
        // Handle React element pattern
        if (React.isValidElement(customElement)) {
            return (_jsx(Slot, { ...restProps, children: React.cloneElement(customElement, {
                    ref,
                    ...(content !== undefined ? { children: content } : {}),
                    ...getDataComponentTagProps(dataComponentTag),
                }) }));
        }
        // Handle render function pattern
        if (typeof customElement === 'function') {
            const renderedElement = customElement(customElementProps, ref);
            if (dataComponentTag) {
                return injectDataComponentTag(renderedElement, dataComponentTag, restProps);
            }
            return _jsx(Slot, { ...restProps, children: renderedElement });
        }
        // Handle render object pattern
        if (typeof customElement === 'object' && 'render' in customElement) {
            const renderedElement = customElement.render(customElementProps, ref);
            if (dataComponentTag) {
                return injectDataComponentTag(renderedElement, dataComponentTag, restProps);
            }
            return _jsx(Slot, { ...restProps, children: renderedElement });
        }
    }
    if (!children) {
        return null;
    }
    if (typeof children === 'string') {
        return (_jsx(Slot, { ref: ref, ...restProps, children: _jsx("div", { ...getDataComponentTagProps(dataComponentTag), children: children }) }));
    }
    if (typeof children === 'function') {
        const renderedElement = children(customElementProps, ref);
        if (dataComponentTag) {
            return injectDataComponentTag(renderedElement, dataComponentTag, restProps);
        }
        return _jsx(Slot, { ...restProps, children: renderedElement });
    }
    if (typeof children === 'object' && 'render' in children) {
        const renderedElement = children.render(customElementProps, ref);
        if (dataComponentTag) {
            return injectDataComponentTag(renderedElement, dataComponentTag, restProps);
        }
        return _jsx(Slot, { ...restProps, children: renderedElement });
    }
    if (React.Children.count(children) > 1) {
        const childrenArray = React.Children.toArray(children);
        const firstChild = childrenArray[0];
        const restChildren = childrenArray.slice(1);
        if (React.isValidElement(firstChild)) {
            // Only inject data-component-tag if the child doesn't already have one
            const existingTag = firstChild.props['data-component-tag'];
            const enhancedFirstChild = React.cloneElement(firstChild, {
                ...getConditionalDataComponentTagProps(dataComponentTag, existingTag),
            });
            return (_jsx(Slot, { ref: ref, ...restProps, children: _jsxs(_Fragment, { children: [enhancedFirstChild, restChildren] }) }));
        }
        return (_jsx(Slot, { ref: ref, ...restProps, children: _jsx(_Fragment, { children: children }) }));
    }
    return (_jsx(Slot, { ref: ref, ...restProps, ...getDataComponentTagProps(dataComponentTag), children: children }));
});
